import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  apiClient,
  canAttemptSilentResume,
  clearAuthSession,
  clearAppClosedAt,
  clearDeviceBinding,
  clearManualLogout,
  getDeviceBindingToken,
  getDeviceId,
  getStoredDeviceId,
  markAppClosedAt,
  markManualLogout,
  setApiAuthHandler,
  setAuthSession
} from '../utils/api';
import { getTranslatedAuthApiError, translateAuthApiMessage } from '../utils/authApiMessage';

export interface User {
  id: string;
  username?: string;
  name?: string;
  email: string;
  avatar?: string;
  bio?: string;
  isAdmin?: boolean;
  role?: string;
  orgId?: number | null;
  orgUserId?: number | null;
  organizations?: { id: number; name: string }[];
}

export interface IdentityState {
  authLevel: number;
  requiresStepUp: boolean;
  isOrgUser: boolean;
  orgId: number | null;
  roles: string[];
  deviceTrusted: boolean;
  sessionCompromised: boolean;
}

type StepUpState = {
  operation: string;
  token: string;
  expiresAt: number;
};

type SecurityState = {
  code:
    | 'none'
    | 'refresh_failed'
    | 'refresh_replay'
    | 'session_compromised'
    | 'device_mismatch'
    | 'device_binding_failed'
    | 'rate_limited'
    | 'temporary_lock';
  message: string;
};

interface AuthContextType {
  user: User | null;
  token: string | null;
  identity: IdentityState;
  isLoading: boolean;
  securityState: SecurityState;
  login: (contact: string, _password?: string, _recaptchaResponse?: string) => Promise<unknown>;
  orgLogin: (contact: string, _password?: string) => Promise<unknown>;
  orgUserLogin: (contact: string, _password?: string) => Promise<unknown>;
  register: (contact: string) => Promise<unknown>;
  verifyEmail: (_email: string, _code: string) => Promise<unknown>;
  requestOtp: (contact: string, opts?: { purpose?: 'login' | 'org_login'; orgId?: number }) => Promise<any>;
  verifyOtp: (
    contact: string,
    code: string,
    opts?: { purpose?: 'login' | 'org_login'; orgId?: number }
  ) => Promise<any>;
  requestStepUp: (operation: string, contact?: string) => Promise<any>;
  verifyStepUp: (operation: string, code: string, contact?: string) => Promise<string>;
  withStepUpToken: (operation: string) => string | null;
  runWithStepUp: <T>(operation: string, request: (stepUpToken?: string) => Promise<T>) => Promise<T>;
  logout: (isManual?: boolean) => Promise<void>;
  updateProfile: (data: Partial<User>) => Promise<void>;
  openLoginDialog: () => void;
  openRegisterDialog: () => void;
  clearSecurityState: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const AUTH_CACHE_KEY = 'auth_identity_cache_v3';
const AUTH_CACHE_TTL_MS = 5 * 60 * 60 * 1000;

const defaultIdentity: IdentityState = {
  authLevel: 0,
  requiresStepUp: false,
  isOrgUser: false,
  orgId: null,
  roles: [],
  deviceTrusted: false,
  sessionCompromised: false
};

const defaultSecurityState: SecurityState = {
  code: 'none',
  message: ''
};

function normalizeSecurityError(error: any): SecurityState {
  const message = (error?.response?.data?.message || error?.message || '').toString();
  if (/too many|limit exceeded/i.test(message)) {
    return { code: 'rate_limited', message };
  }
  if (/locked/i.test(message)) {
    return { code: 'temporary_lock', message };
  }
  return { code: 'none', message: '' };
}

function cacheIdentity(payload: { user: User | null; identity: IdentityState }) {
  localStorage.setItem(AUTH_CACHE_KEY, JSON.stringify({ ...payload, cachedAt: Date.now() }));
}

function loadCachedIdentity(): { user: User | null; identity: IdentityState } | null {
  const raw = localStorage.getItem(AUTH_CACHE_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as { user: User | null; identity: IdentityState; cachedAt?: number };
    if (!parsed.cachedAt || Date.now() - parsed.cachedAt > AUTH_CACHE_TTL_MS) {
      localStorage.removeItem(AUTH_CACHE_KEY);
      return null;
    }
    return parsed;
  } catch (_error) {
    localStorage.removeItem(AUTH_CACHE_KEY);
    return null;
  }
}

function decodeJwtPayload(token?: string | null): any | null {
  if (!token || token.split('.').length < 2) return null;
  try {
    const payloadSegment = token.split('.')[1];
    const normalized = payloadSegment.replace(/-/g, '+').replace(/_/g, '/');
    const decoded = atob(normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '='));
    return JSON.parse(decoded);
  } catch {
    return null;
  }
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [identity, setIdentity] = useState<IdentityState>(defaultIdentity);
  const [isLoading, setIsLoading] = useState(true);
  const [securityState, setSecurityState] = useState<SecurityState>(defaultSecurityState);
  const [stepUpMap, setStepUpMap] = useState<Record<string, StepUpState>>({});

  const clearSecurityState = useCallback(() => {
    setSecurityState(defaultSecurityState);
  }, []);

  const hardReset = useCallback(() => {
    setToken(null);
    setUser(null);
    setIdentity(defaultIdentity);
    setStepUpMap({});
    clearAuthSession();
    localStorage.removeItem(AUTH_CACHE_KEY);
  }, []);

  const applyAuthPayload = useCallback((payload: any) => {
    const nextToken = payload?.accessToken || null;
    const nextBinding = payload?.deviceBindingToken || getDeviceBindingToken();
    const claims = decodeJwtPayload(nextToken);
    const fallbackUser = claims
      ? ({
          id: String(claims.id || ''),
          username: claims.username,
          email: claims.email || '',
          isAdmin: !!claims.isAdmin,
          role: claims.role,
          orgId: claims.orgId || null,
          orgUserId: claims.orgUserId || null
        } as User)
      : null;
    const nextUser = (payload?.user || fallbackUser || null) as User | null;

    const nextIdentity: IdentityState = {
      authLevel: Number(payload?.authLevel || claims?.authLevel || 0),
      requiresStepUp: false,
      isOrgUser: !!(nextUser?.orgId || nextUser?.orgUserId),
      orgId: nextUser?.orgId || null,
      roles: [nextUser?.role || (nextUser?.isAdmin ? 'admin' : 'user')].filter(Boolean),
      deviceTrusted: !!nextBinding,
      sessionCompromised: false
    };

    setToken(nextToken);
    setUser(nextUser);
    setIdentity(nextIdentity);

    setAuthSession({
      accessToken: nextToken,
      deviceBindingToken: nextBinding,
      deviceId: getStoredDeviceId()
    });

    clearManualLogout();
    clearAppClosedAt();
    cacheIdentity({ user: nextUser, identity: nextIdentity });
  }, []);

  useEffect(() => {
    setApiAuthHandler({
      onAuthFailure: (reason, detail) => {
        if (reason === 'refresh_replay' || reason === 'session_compromised') {
          setSecurityState({ code: reason, message: detail || 'Your session was revoked for security reasons.' });
          setIdentity((prev) => ({ ...prev, sessionCompromised: true }));
          hardReset();
          return;
        }
        if (reason === 'device_mismatch' || reason === 'device_binding_failed') {
          setSecurityState({ code: reason, message: detail || 'Device mismatch detected. Please login again.' });
          clearDeviceBinding();
          hardReset();
          return;
        }
        setSecurityState({ code: 'refresh_failed', message: detail || 'Session expired. Please login again.' });
        hardReset();
      }
    });

    const bootstrap = async () => {
      const cached = loadCachedIdentity();
      if (cached) {
        setUser(cached.user);
        setIdentity({
          ...cached.identity
        });
      }

      setAuthSession({
        accessToken: null,
        deviceBindingToken: getDeviceBindingToken(),
        deviceId: getStoredDeviceId()
      });

      if (canAttemptSilentResume()) {
        try {
          const response = await apiClient.post('/api/auth/token/refresh', {
            deviceId: getDeviceId(),
            deviceBindingToken: getDeviceBindingToken()
          });
          applyAuthPayload(response.data);
        } catch (_error) {
          if (!cached) {
            hardReset();
          }
        }
      } else if (!cached) {
        hardReset();
      }

      setIsLoading(false);
    };

    const onClose = () => {
      markAppClosedAt();
    };

    window.addEventListener('pagehide', onClose);
    window.addEventListener('beforeunload', onClose);

    void bootstrap();
    return () => {
      window.removeEventListener('pagehide', onClose);
      window.removeEventListener('beforeunload', onClose);
    };
  }, [applyAuthPayload, hardReset]);

  const requestOtp = useCallback(
    async (contact: string, opts?: { purpose?: 'login' | 'org_login'; orgId?: number }) => {
      clearSecurityState();
      try {
        const response = await apiClient.post('/api/auth/otp/request', {
          contact,
          purpose: opts?.purpose || 'login',
          ...(opts?.orgId ? { orgId: opts.orgId } : {}),
          deviceId: getDeviceId(),
          deviceBindingToken: getDeviceBindingToken()
        });

        const payload = response.data as { deviceBindingToken?: string };
        if (payload.deviceBindingToken) {
          setAuthSession({ deviceBindingToken: payload.deviceBindingToken, deviceId: getDeviceId() });
          setIdentity((prev) => ({ ...prev, authLevel: 1, deviceTrusted: true }));
        }

        return response.data;
      } catch (error: any) {
        const security = normalizeSecurityError(error);
        if (security.code !== 'none') setSecurityState(security);
        throw new Error(getTranslatedAuthApiError(error, 'auth.requestFailed'));
      }
    },
    [clearSecurityState]
  );

  const verifyOtp = useCallback(
    async (contact: string, code: string, opts?: { purpose?: 'login' | 'org_login'; orgId?: number }) => {
      clearSecurityState();
      try {
        const response = await apiClient.post('/api/auth/otp/verify', {
          contact,
          code,
          purpose: opts?.purpose || 'login',
          ...(opts?.orgId ? { orgId: opts.orgId } : {}),
          deviceId: getDeviceId(),
          deviceBindingToken: getDeviceBindingToken()
        });
        applyAuthPayload(response.data);
        return response.data;
      } catch (error: any) {
        const security = normalizeSecurityError(error);
        if (security.code !== 'none') setSecurityState(security);
        throw new Error(getTranslatedAuthApiError(error, 'auth.invalidCredentials'));
      }
    },
    [applyAuthPayload, clearSecurityState]
  );

  const login = useCallback(
    async (contact: string) => {
      return requestOtp(contact, { purpose: 'login' });
    },
    [requestOtp]
  );

  const orgLogin = useCallback(
    async (contact: string) => {
      return requestOtp(contact, { purpose: 'org_login' });
    },
    [requestOtp]
  );

  const orgUserLogin = useCallback(
    async (contact: string) => {
      return requestOtp(contact, { purpose: 'org_login' });
    },
    [requestOtp]
  );

  const register = useCallback(async (contact: string) => {
    clearSecurityState();
    try {
      const response = await apiClient.post('/api/auth/register', { contact });
      const data = response.data as { message?: string };
      return {
        ...data,
        message: translateAuthApiMessage(data?.message || null, 'auth.accountCreatedCredentialsSent')
      };
    } catch (error: any) {
      throw new Error(getTranslatedAuthApiError(error, 'auth.registrationFailed'));
    }
  }, [clearSecurityState]);

  const verifyEmail = useCallback(async () => {
    throw new Error('Legacy verify-email flow is disabled. Use OTP login.');
  }, []);

  const requestStepUp = useCallback(
    async (operation: string, contact?: string) => {
      if (!user?.email) throw new Error('User context not found for step-up.');
      const response = await apiClient.post('/api/auth/step-up/request', {
        operation,
        contact: contact || user.email,
        deviceId: getDeviceId(),
        deviceBindingToken: getDeviceBindingToken()
      });
      const payload = response.data as { deviceBindingToken?: string };
      if (payload.deviceBindingToken) {
        setAuthSession({ deviceBindingToken: payload.deviceBindingToken, deviceId: getDeviceId() });
      }
      return response.data;
    },
    [user?.email]
  );

  const verifyStepUp = useCallback(
    async (operation: string, code: string, contact?: string) => {
      if (!user?.email) throw new Error('User context not found for step-up.');
      const response = await apiClient.post('/api/auth/step-up/verify', {
        operation,
        code,
        contact: contact || user.email,
        deviceId: getDeviceId(),
        deviceBindingToken: getDeviceBindingToken()
      });
      const data = response.data as { stepUpToken: string };
      const expiresAt = Date.now() + 5 * 60 * 1000;
      setStepUpMap((prev) => ({
        ...prev,
        [operation]: {
          operation,
          token: data.stepUpToken,
          expiresAt
        }
      }));
      return data.stepUpToken;
    },
    [user?.email]
  );

  const withStepUpToken = useCallback(
    (operation: string) => {
      const entry = stepUpMap[operation];
      if (!entry) return null;
      if (entry.expiresAt <= Date.now()) {
        setStepUpMap((prev) => {
          const copy = { ...prev };
          delete copy[operation];
          return copy;
        });
        return null;
      }
      return entry.token;
    },
    [stepUpMap]
  );

  const runWithStepUp = useCallback(
    async <T,>(operation: string, request: (stepUpToken?: string) => Promise<T>): Promise<T> => {
      const tokenForOp = withStepUpToken(operation);
      try {
        return await request(tokenForOp || undefined);
      } catch (error: any) {
        if (error?.requiresStepUp) {
          setIdentity((prev) => ({ ...prev, requiresStepUp: true }));
          throw Object.assign(new Error('Step-up authentication required'), {
            requiresStepUp: true,
            operation
          });
        }
        throw error;
      }
    },
    [withStepUpToken]
  );

  const logout = useCallback(async (isManual: boolean = true) => {
    try {
      await apiClient.post('/api/auth/token/revoke');
    } catch (_error) {
      // Keep logout idempotent.
    } finally {
      if (isManual) {
        sessionStorage.removeItem('lastPath');
        markManualLogout();
      }
      hardReset();
    }
  }, [hardReset]);

  const updateProfile = useCallback(async (data: Partial<User>) => {
    const response = await apiClient.put('/api/auth/profile', data);
    const profileData = response.data as { user: User };
    setUser(profileData.user);
    cacheIdentity({ user: profileData.user, identity });
  }, [identity]);

  const openLoginDialog = useCallback(() => {
    window.location.href = '/login';
  }, []);

  const openRegisterDialog = useCallback(() => {
    window.location.href = '/register';
  }, []);

  const contextValue = useMemo<AuthContextType>(
    () => ({
      user,
      token,
      identity,
      isLoading,
      securityState,
      login,
      orgLogin,
      orgUserLogin,
      register,
      verifyEmail,
      requestOtp,
      verifyOtp,
      requestStepUp,
      verifyStepUp,
      withStepUpToken,
      runWithStepUp,
      logout,
      updateProfile,
      openLoginDialog,
      openRegisterDialog,
      clearSecurityState
    }),
    [
      user,
      token,
      identity,
      isLoading,
      securityState,
      login,
      orgLogin,
      orgUserLogin,
      register,
      verifyEmail,
      requestOtp,
      verifyOtp,
      requestStepUp,
      verifyStepUp,
      withStepUpToken,
      runWithStepUp,
      logout,
      updateProfile,
      openLoginDialog,
      openRegisterDialog,
      clearSecurityState
    ]
  );

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
};
